import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { FetchResult, useMutation, useQuery } from '@apollo/client'

import { useFlag } from '@unleash/proxy-client-react'
import DOMPurify from 'dompurify'

import { useSelectedServiceFkey, useSelectedServiceName } from '~/contexts/Service'
import { UserContext } from '~/contexts/User'
import { AI_POST_CONTENT } from '~/pages/Observations/mutations'
import { GET_AI_ASSISTANT_QUESTION_PROMPTS } from '~/pages/Observations/queries'
import { generateUuid, simulateFetch } from '~/utils'

import { aiResponsePrefix, userMessagePrefix } from '../constants/assistPrefixes'

import { AssistActions, AssistModalProps, Question } from '../interface'

const useAssistant = ({
  postId,
  postTitle,
  taggedChildren,
  postDescription,
  postRichTextDescription,
  assistAction,
  closeAssistModal,
  onChange,
}: AssistModalProps) => {
  const user = useContext(UserContext)
  const userName = user.state!.displayName
  const firstRenderRef = useRef(true)
  const serviceFkey = useSelectedServiceFkey()
  const serviceName = useSelectedServiceName()
  const [isFetching, setIsFetching] = useState(true)
  const [messages, setMessages] = useState<Playground.AssistantConversationMessage[]>([])
  const [textInputEnabled, enableTextInput] = useState(false)
  const [currentUserRequest, setCurrentUserRequest] = useState<{
    action: Playground.AssistActions
    userMessage: string
  }>({
    action: assistAction,
    userMessage: postDescription,
  })
  const generateLearningV2 =
    useFlag('edu-playground-learning-documentation') && currentUserRequest.action === AssistActions.GENERATE
  const welcomeMessageDisplayed = useRef(false)
  const feedbackMessageDisplayed = useRef(false)
  const helpQuestionnaireDisplayed = useRef(false)
  const userAnswers = useRef<Record<string, any>>({})
  const [aiPostContent] = useMutation(AI_POST_CONTENT)

  const {
    data: aiPrompts,
    loading: aiPromptsLoading,
    error: aiPromptsError,
  } = useQuery(GET_AI_ASSISTANT_QUESTION_PROMPTS, {
    variables: {
      serviceFkey,
      version: generateLearningV2 ? 'v2' : 'v1',
    },
  })

  const { helpQuestionnaire, mainQuestionnaire, welcomeMessage, feedbackMessage } = useMemo(() => {
    const aiQuestions: {
      changeTone?: Question[]
      generateLearning?: Question[]
      contentManipulation?: Question[]
      helperText?: Question[]
    } = aiPrompts?.service?.aiAssistantQuestionPrompts?.questions

    if (aiPromptsLoading || aiPromptsError || !aiQuestions) {
      return {
        mainQuestionnaire: undefined,
        welcomeMessage: '',
        feedbackMessage: '',
      }
    }

    const mainQuestionnaire = {
      [AssistActions.SPELLING]: undefined,
      [AssistActions.EXPAND]: undefined,
      [AssistActions.TRIM]: undefined,
      [AssistActions.TRANSLATE]: undefined,
      [AssistActions.GENERATE]: aiQuestions?.generateLearning,
      [AssistActions.TONE]: aiQuestions?.changeTone,
    }[currentUserRequest.action]

    const { contentManipulation } = aiQuestions
    let welcomeMessage = ''
    let feedbackMessage = ''

    if (contentManipulation) {
      for (const { questionType, question } of contentManipulation) {
        if (questionType === 'welcomeMessage') {
          welcomeMessage = question || ''
        } else if (questionType === 'actionMessage') {
          feedbackMessage = question || ''
        }
      }
    }

    return {
      helpQuestionnaire: aiQuestions?.helperText,
      mainQuestionnaire,
      welcomeMessage,
      feedbackMessage,
    }
  }, [aiPrompts, aiPromptsError, aiPromptsLoading, currentUserRequest])

  const mainQuestionnaireCurrentIndex = useRef(-1)

  const onInsert = useCallback(
    (newContent: string) => {
      const newDescription = `${postDescription}\n\n${newContent}`
      const richTextContent = newContent.replace(/\n/g, '<br>')
      const newRichTextDescription = `${postRichTextDescription}<br/><br/>${richTextContent}`
      onChange({
        name: 'description',
        value: newDescription,
      })
      onChange({
        name: 'richTextDescription',
        value: newRichTextDescription,
      })
      closeAssistModal('ON_INSERT')
    },
    [closeAssistModal, onChange, postDescription, postRichTextDescription]
  )

  const handleFinalContentAssistActionPill = useCallback(
    (pill: Playground.AssistActionPill & Playground.AssistActionPillFinalContent) => {
      if (pill.action === 'INSERT') {
        onInsert(pill.content)
        return
      }

      firstRenderRef.current = true // reset the first render flag to true
      userAnswers.current = {} // reset user answers for the new action
      setCurrentUserRequest({
        action: pill.action,
        userMessage: pill.content,
      })
    },
    [onInsert]
  )

  const finalContentActionPills = useCallback(
    (content: string): Playground.AssistActionPill[] => {
      const insertAction: Playground.AssistActionPill = {
        label: 'Insert into post',
        type: 'FINAL_CONTENT',
        action: 'INSERT',
        icon: 'download',
        content,
      }
      if (generateLearningV2) {
        // Action pills for Generate Learning
        return [
          insertAction,
          {
            label: 'Start a new story',
            type: 'FINAL_CONTENT',
            action: AssistActions.GENERATE,
            content,
          },
        ]
      } else {
        // Action pills for all other assist actions - use AI response content for further processing
        return [
          insertAction,
          {
            label: 'Spelling & grammar',
            type: 'FINAL_CONTENT',
            action: AssistActions.SPELLING,
            content,
          },
          {
            label: 'Expand upon',
            type: 'FINAL_CONTENT',
            action: AssistActions.EXPAND,
            content,
          },
          {
            label: 'Trim content',
            type: 'FINAL_CONTENT',
            action: AssistActions.TRIM,
            content,
          },
          {
            label: 'Change tone',
            type: 'FINAL_CONTENT',
            action: AssistActions.TONE,
            content,
          },
          {
            label: 'Translate',
            type: 'FINAL_CONTENT',
            action: AssistActions.TRANSLATE,
            content,
          },
        ]
      }
    },
    [generateLearningV2]
  )

  const createAIMessage = useCallback(
    (props: {
      boldText?: string
      regularText?: string
      isError?: boolean
      isWelcomeMessage?: boolean
      actionPills?: Playground.AssistActionPill[]
    }) => {
      const { boldText = '', regularText = '', isError = false, isWelcomeMessage, actionPills } = props

      let displayFeedbackMessage = false
      const shouldShowFeedback = currentUserRequest.action !== AssistActions.GENERATE

      // if this is first ai generated response then display the feedback message once
      if (
        mainQuestionnaireCurrentIndex.current === -1 &&
        !feedbackMessageDisplayed.current &&
        !isError &&
        !isWelcomeMessage &&
        shouldShowFeedback
      ) {
        displayFeedbackMessage = true
        feedbackMessageDisplayed.current = true
      }

      setMessages((previousMessages) => [
        ...previousMessages,
        {
          id: generateUuid(),
          timestamp: new Date().toISOString(),
          content: {
            boldText,
            regularText,
          },
          sender: 'AI',
          withTextReveal: true,
          isError,
          feedbackMessage: displayFeedbackMessage ? feedbackMessage : undefined,
          isWelcomeMessage,
          actionPills,
        },
      ])

      if (isError) return

      // if no main questionnaire is defined, we can return early
      if (!mainQuestionnaire) return

      // if current question expects user input then enable input and return
      const currentQuestion = mainQuestionnaire?.[mainQuestionnaireCurrentIndex.current]
      if (currentQuestion?.questionType) {
        enableTextInput(!currentQuestion?.actions?.length)
        return
      }

      // if there is a next question, set it as current and display it
      if (mainQuestionnaire?.length && mainQuestionnaireCurrentIndex.current >= 0) {
        const nextQuestionIndex = mainQuestionnaireCurrentIndex.current + 1
        if (nextQuestionIndex < mainQuestionnaire.length) {
          mainQuestionnaireCurrentIndex.current = nextQuestionIndex
          const nextQuestion = mainQuestionnaire[nextQuestionIndex]
          createAIMessage(
            generateLearningV2 ? { regularText: nextQuestion.question } : { boldText: nextQuestion.question }
          )
        } else mainQuestionnaireCurrentIndex.current = -1
      }
    },
    [currentUserRequest, feedbackMessage, generateLearningV2, mainQuestionnaire]
  )

  const createUserMessage = useCallback((boldText: string, regularText: string) => {
    setMessages((previousMessages) => [
      ...previousMessages,
      {
        id: generateUuid(),
        timestamp: new Date().toISOString(),
        content: {
          boldText,
          regularText,
        },
        sender: 'USER',
        withTextReveal: false,
      },
    ])
  }, [])

  const createErrorMessage = useCallback(
    (errorMessage?: string) => {
      createAIMessage({
        isError: true,
        boldText: aiResponsePrefix.ERROR,
        regularText: errorMessage?.trim() || defaultErrorMessage,
      })
    },
    [createAIMessage]
  )

  const helperActionPills = useCallback(
    (pills: Question['actions']): Playground.AssistActionPill[] =>
      pills?.map((pill) => ({
        label: pill.title,
        type: 'ASK_QUESTION',
        questionId: pill.questionId,
        questionSet: pill.questionSet,
        content: pill.title,
      })) || [],
    []
  )

  const getAIResponse = useCallback(async (): Promise<FetchResult> => {
    setIsFetching(true)
    enableTextInput(false)
    try {
      const simulatedPreMessageFetchPromise = !welcomeMessageDisplayed.current
        ? simulateFetch(Math.random())
        : Promise.resolve()
      const aiPostContentPromise = aiPostContent({
        variables: {
          action: currentUserRequest.action,
          service: serviceName,
          content: currentUserRequest.userMessage,
          ...(currentUserRequest.action === AssistActions.GENERATE.toString()
            ? {
                story: {
                  ...userAnswers.current,
                  title: postTitle?.trim(),
                  children: taggedChildren,
                  user: userName,
                  time: new Date().toDateString() + ', ' + new Date().toLocaleTimeString(),
                },
              }
            : {}),
          ...(currentUserRequest.action === AssistActions.TONE.toString() ? userAnswers.current : {}),
          isTryAgain: false,
          postId,
          serviceFkey,
        },
      })

      // wait for the simulated fetch to complete and then show the welcome message
      if (
        !welcomeMessageDisplayed.current &&
        welcomeMessage &&
        ![AssistActions.GENERATE, AssistActions.TONE].includes(currentUserRequest.action)
      ) {
        await simulatedPreMessageFetchPromise
        createAIMessage({ boldText: welcomeMessage, isWelcomeMessage: true })
      }
      // set the welcome message as displayed
      welcomeMessageDisplayed.current = true

      // wait for the AI response
      const result = await aiPostContentPromise
      const sanitizedContent = DOMPurify.sanitize(result.data?.generateAiContent.body)
      const responseCode: number = result.data?.generateAiContent.aiResponseCode
      const isError = responseCode !== 200

      // if the AI response is an error, create an error message
      if (isError) {
        const errorMessage = sanitizedContent || result.data?.generateAiContent.errorMessage
        createErrorMessage(errorMessage)
      } else {
        createAIMessage({
          boldText: (generateLearningV2 ? '📚 ' : '') + aiResponsePrefix[currentUserRequest.action] || '',
          regularText: sanitizedContent,
          actionPills: !isError ? finalContentActionPills(sanitizedContent) : undefined,
        })
      }

      return Promise.resolve({ data: { content: '' } }) // this is sent to input box and we are handling error in catch block below
    } catch (error) {
      console.error('Error in getAIResponse', error)
      createErrorMessage()
      return Promise.resolve({ data: { content: '' } }) // this is sent to input box as we are handling error in catch block
    } finally {
      setIsFetching(false)
    }
  }, [
    aiPostContent,
    currentUserRequest,
    serviceName,
    postTitle,
    taggedChildren,
    userName,
    postId,
    serviceFkey,
    welcomeMessage,
    createAIMessage,
    createErrorMessage,
    generateLearningV2,
    finalContentActionPills,
  ])

  const mainQuestionnaireActionPills = useCallback(
    (pills: Question['actions']): Playground.AssistActionPill[] =>
      pills?.map((pill) => ({
        label: pill.title,
        type: 'SAVE_ANSWER',
        value: pill.value,
        content: pill.title,
      })) || [],
    []
  )

  const handleUserInput = useCallback(
    async (message: string, value?: boolean) => {
      enableTextInput(false)
      const sanitizedMessage = DOMPurify.sanitize(message)
      createUserMessage('', sanitizedMessage)

      const currentIndex = mainQuestionnaireCurrentIndex.current
      // Store the answer if we have a question type
      if (mainQuestionnaire && currentIndex >= 0 && currentIndex < mainQuestionnaire.length) {
        const currentQuestion = mainQuestionnaire[currentIndex]
        if (currentQuestion.questionType) {
          const answer = value !== undefined ? value : sanitizedMessage
          userAnswers.current[currentQuestion.questionType] = answer
        }
      }

      const nextQuestionIndex = mainQuestionnaireCurrentIndex.current + 1
      // get AI response if there are no more questions or if the next question index is out of bounds
      if (!mainQuestionnaire?.length || nextQuestionIndex >= mainQuestionnaire.length)
        return await getAIResponse()

      // ask next question
      mainQuestionnaireCurrentIndex.current = nextQuestionIndex
      setIsFetching(true)
      await simulateFetch(Math.random())
      createAIMessage(
        generateLearningV2
          ? {
              regularText: mainQuestionnaire[nextQuestionIndex].question,
              actionPills: mainQuestionnaire[nextQuestionIndex].actions?.length
                ? mainQuestionnaireActionPills(mainQuestionnaire[nextQuestionIndex].actions)
                : undefined,
            }
          : { boldText: mainQuestionnaire[nextQuestionIndex].question }
      )
      setIsFetching(false)
      return Promise.resolve({ data: { content: '' } }) // this is sent to input box
    },
    [
      createAIMessage,
      createUserMessage,
      generateLearningV2,
      getAIResponse,
      mainQuestionnaire,
      mainQuestionnaireActionPills,
    ]
  )

  const handleHelpQuestionnaireActionPills = useCallback(
    (pill: Playground.AssistActionPill & Playground.AssistActionPillAskQuestion) => {
      enableTextInput(false)
      createUserMessage('', pill.content)

      if (pill.questionSet === 'helperText' && helpQuestionnaire) {
        const nextHelpQuestion = helpQuestionnaire.find((q) => q.id === pill.questionId)
        if (!nextHelpQuestion) {
          createErrorMessage()
          return
        }
        createAIMessage({
          regularText: nextHelpQuestion.question,
          actionPills: nextHelpQuestion.actions?.length
            ? helperActionPills(nextHelpQuestion.actions)
            : undefined,
        })
      } else if (pill.questionSet === 'generateLearning' && mainQuestionnaire) {
        mainQuestionnaireCurrentIndex.current = 0
        const firstMainQuestion = mainQuestionnaire[0]
        createAIMessage({ regularText: firstMainQuestion.question })
        if (!firstMainQuestion.actions?.length || !firstMainQuestion.actions[0].value.trim())
          enableTextInput(true)
      } else {
        createErrorMessage()
      }
    },
    [
      createAIMessage,
      createErrorMessage,
      createUserMessage,
      helpQuestionnaire,
      helperActionPills,
      mainQuestionnaire,
    ]
  )

  const handleAssistActionPill = useCallback(
    (pill: Playground.AssistActionPill) => {
      switch (pill.type) {
        case 'ASK_QUESTION':
          handleHelpQuestionnaireActionPills(pill)
          break
        case 'FINAL_CONTENT':
          handleFinalContentAssistActionPill(pill)
          break
        case 'SAVE_ANSWER':
          handleUserInput(pill.content, Boolean(Number(pill.value)))
          break
        default:
          console.error('Unknown pill type:', JSON.stringify(pill, null, 2))
          createErrorMessage()
          break
      }
    },
    [
      createErrorMessage,
      handleFinalContentAssistActionPill,
      handleHelpQuestionnaireActionPills,
      handleUserInput,
    ]
  )

  const initialMessage = useCallback(() => {
    setIsFetching(false)
    const { sender, boldText, regularText } = firstMessageContent(
      currentUserRequest.action,
      currentUserRequest.userMessage
    )
    if (sender === 'USER') createUserMessage(boldText, regularText)

    if (generateLearningV2 && helpQuestionnaire?.length && !helpQuestionnaireDisplayed.current) {
      // If Generate Learning V2 is enabled and help questionnaire exists, show the help questions
      helpQuestionnaireDisplayed.current = true
      const firstHelpQuestion = helpQuestionnaire[0]
      createAIMessage({
        regularText: firstHelpQuestion.question,
        actionPills: firstHelpQuestion.actions?.length
          ? helperActionPills(firstHelpQuestion.actions)
          : undefined,
      })
    } else if (mainQuestionnaire?.length) {
      mainQuestionnaireCurrentIndex.current = 0
      const firstQuestion = mainQuestionnaire[0]
      generateLearningV2 && createUserMessage('', 'Start a new story')
      createAIMessage(
        generateLearningV2 ? { regularText: firstQuestion.question } : { boldText: firstQuestion.question }
      )
    } else {
      getAIResponse()
    }
  }, [
    currentUserRequest,
    createUserMessage,
    generateLearningV2,
    helpQuestionnaire,
    mainQuestionnaire,
    createAIMessage,
    helperActionPills,
    getAIResponse,
  ])

  // Initialize the conversation
  useEffect(() => {
    if (!firstRenderRef.current || aiPromptsLoading || !currentUserRequest.action) return
    firstRenderRef.current = false
    initialMessage()
  }, [aiPromptsLoading, initialMessage, currentUserRequest])

  return {
    messages,
    isFetching,
    handleUserInput,
    textInputEnabled,
    handleAssistActionPill,
  }
}

export default useAssistant

const firstMessageContent = (
  assistAction: Playground.AssistActions,
  content: string
): {
  sender: string
  boldText: string
  regularText: string
} => {
  const prefixMap = {
    [AssistActions.SPELLING]: {
      sender: 'USER',
      boldText: userMessagePrefix[AssistActions.SPELLING],
      regularText: content,
    },
    [AssistActions.EXPAND]: {
      sender: 'USER',
      boldText: userMessagePrefix[AssistActions.EXPAND],
      regularText: content,
    },
    [AssistActions.TRIM]: {
      sender: 'USER',
      boldText: userMessagePrefix[AssistActions.TRIM],
      regularText: content,
    },
    [AssistActions.TRANSLATE]: {
      sender: 'USER',
      boldText: userMessagePrefix[AssistActions.TRANSLATE],
      regularText: content,
    },
    [AssistActions.GENERATE]: {
      sender: 'AI',
      boldText: '',
      regularText: '',
    },
    [AssistActions.TONE]: {
      sender: 'USER',
      boldText: userMessagePrefix[AssistActions.TONE],
      regularText: content,
    },
  }

  return (
    prefixMap[assistAction] || {
      sender: 'AI',
      boldText: 'ERROR: ',
      regularText: "Can't find details, please contact support.",
    }
  )
}

const defaultErrorMessage =
  'Something went wrong on our end. Check your connection and try again. If this keeps happening, please contact support.'
